import { useState, useEffect } from 'react';
import Modal from '../common/Modal';
import Button from '../common/Button';

const decodeHtmlEntities = (str) => {
    if (!str) return str;
    const textarea = document.createElement('textarea');
    textarea.innerHTML = str;
    return textarea.value;
};

function FilterModal({ config, currentFilters, onApply, onClose }) {
    const [localFilters, setLocalFilters] = useState({ ...currentFilters });

    useEffect(() => {
        setLocalFilters({ ...currentFilters });
    }, [currentFilters]);

    const handleFilterChange = (fieldId, value) => {
        setLocalFilters(prev => {
            // If value is empty, remove the filter
            if (!value || (Array.isArray(value) && value.length === 0) || 
                (typeof value === 'string' && value.trim() === '')) {
                const newFilters = { ...prev };
                delete newFilters[fieldId];
                return newFilters;
            }
            
            return {
                ...prev,
                [fieldId]: value
            };
        });
    };

    const handleApply = () => {
        onApply(localFilters);
    };

    const handleClear = () => {
        setLocalFilters({});
    };

    const renderFilterField = (filter) => {
        const currentValue = localFilters[filter.field_id] || '';
        const filterType = filter.filter_type || filter.type; // Use configured filter_type or fallback to field type
        const filterLabel = decodeHtmlEntities(filter.label || filter.name);
        
        // Handle the case where checkbox/multiselect fields are configured to display as 'select' (which means multi-select dropdown in admin)
        const isMultiValueField = ['checkbox', 'multiselectbox', 'multiselect'].includes(filter.type);
        const effectiveFilterType = (filterType === 'select' && isMultiValueField) ? 'multiselect' : filterType;
        
        // Special handling for datebox fields rendered as checkbox (has value check)
        // Check both original_type (from BP) and type (mapped) for backward compatibility
        const isDateboxCheckbox = (filter.original_type === 'datebox' || filter.type === 'date') && effectiveFilterType === 'checkbox';

        switch (effectiveFilterType) {
            case 'select':
                return (
                    <div key={filter.field_id} className="filter-field">
                        <label htmlFor={filter.field_id} className="filter-label">{filterLabel}</label>
                        <select
                            id={filter.field_id}
                            value={currentValue}
                            onChange={(e) => handleFilterChange(filter.field_id, e.target.value)}
                            className="filter-select"
                        >
                            <option value="">Any</option>
                            {filter.options?.map(option => (
                                <option key={option.value} value={option.value}>
                                    {decodeHtmlEntities(option.label)}
                                </option>
                            ))}
                        </select>
                    </div>
                );

            case 'multiselect':
                return (
                    <div key={filter.field_id} className="filter-field">
                        <label htmlFor={filter.field_id} className="filter-label">{filterLabel}</label>
                        <select
                            id={filter.field_id}
                            value={Array.isArray(currentValue) ? currentValue : []}
                            onChange={(e) => {
                                const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                                handleFilterChange(filter.field_id, selectedOptions);
                            }}
                            multiple={true}
                            className="filter-select"
                        >
                            {filter.options?.map(option => (
                                <option key={option.value} value={option.value}>
                                    {decodeHtmlEntities(option.label)}
                                </option>
                            ))}
                        </select>
                    </div>
                );

            case 'checkbox':
                // Special case: datebox field as checkbox (single checkbox for "has value")
                if (isDateboxCheckbox) {
                    return (
                        <div key={filter.field_id} className="filter-field">
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={currentValue === 'has_value' || currentValue === true}
                                    onChange={(e) => {
                                        handleFilterChange(filter.field_id, e.target.checked ? 'has_value' : '');
                                    }}
                                />
                                {filterLabel}
                            </label>
                        </div>
                    );
                }
                
                // Regular checkbox field with multiple options
                return (
                    <div key={filter.field_id} className="filter-field checkbox-group">
                        <span className="filter-label">{filterLabel}</span>
                        <div className="checkbox-options">
                            {filter.options?.map(option => (
                                <label key={option.value} className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        value={option.value}
                                        checked={Array.isArray(currentValue) 
                                            ? currentValue.includes(option.value)
                                            : currentValue === option.value
                                        }
                                        onChange={(e) => {
                                            let newValue;
                                            if (Array.isArray(currentValue)) {
                                                if (e.target.checked) {
                                                    newValue = [...currentValue, option.value];
                                                } else {
                                                    newValue = currentValue.filter(v => v !== option.value);
                                                }
                                            } else {
                                                newValue = e.target.checked ? [option.value] : [];
                                            }
                                            handleFilterChange(filter.field_id, newValue);
                                        }}
                                    />
                                    {decodeHtmlEntities(option.label)}
                                </label>
                            ))}
                        </div>
                    </div>
                );

            case 'radio':
                return (
                    <div key={filter.field_id} className="filter-field radio-group">
                        <span className="filter-label">{filterLabel}</span>
                        <div className="radio-options">
                            <label className="radio-label">
                                <input
                                    type="radio"
                                    name={`filter_${filter.field_id}`}
                                    value=""
                                    checked={!currentValue}
                                    onChange={(e) => handleFilterChange(filter.field_id, e.target.value)}
                                />
                                Any
                            </label>
                            {filter.options?.map(option => (
                                <label key={option.value} className="radio-label">
                                    <input
                                        type="radio"
                                        name={`filter_${filter.field_id}`}
                                        value={option.value}
                                        checked={currentValue === option.value}
                                        onChange={(e) => handleFilterChange(filter.field_id, e.target.value)}
                                    />
                                    {decodeHtmlEntities(option.label)}
                                </label>
                            ))}
                        </div>
                    </div>
                );

            case 'text':
            case 'search':
                return (
                    <div key={filter.field_id} className="filter-field">
                        <label htmlFor={filter.field_id} className="filter-label">{filterLabel}</label>
                        <input
                            type="text"
                            id={filter.field_id}
                            value={currentValue}
                            onChange={(e) => handleFilterChange(filter.field_id, e.target.value)}
                            placeholder={`Search ${filterLabel.toLowerCase()}...`}
                            className="filter-input"
                        />
                    </div>
                );

            case 'number':
                return (
                    <div key={filter.field_id} className="filter-field">
                        <label htmlFor={filter.field_id} className="filter-label">{filterLabel}</label>
                        <input
                            type="number"
                            id={filter.field_id}
                            value={currentValue}
                            onChange={(e) => handleFilterChange(filter.field_id, e.target.value)}
                            className="filter-input"
                        />
                    </div>
                );

            case 'date':
                return (
                    <div key={filter.field_id} className="filter-field">
                        <label htmlFor={filter.field_id} className="filter-label">{filterLabel}</label>
                        <input
                            type="date"
                            id={filter.field_id}
                            value={currentValue}
                            onChange={(e) => handleFilterChange(filter.field_id, e.target.value)}
                            className="filter-input"
                        />
                    </div>
                );

            case 'range':
                return (
                    <div key={filter.field_id} className="filter-field range-field">
                        <span className="filter-label">{filterLabel}</span>
                        <div className="range-inputs">
                            <input
                                type="number"
                                placeholder="Min"
                                value={currentValue?.min || ''}
                                onChange={(e) => handleFilterChange(filter.field_id, {
                                    ...currentValue,
                                    min: e.target.value
                                })}
                                className="filter-input range-input"
                            />
                            <span className="range-separator">to</span>
                            <input
                                type="number"
                                placeholder="Max"
                                value={currentValue?.max || ''}
                                onChange={(e) => handleFilterChange(filter.field_id, {
                                    ...currentValue,
                                    max: e.target.value
                                })}
                                className="filter-input range-input"
                            />
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    const activeFilterCount = Object.keys(localFilters).filter(key => {
        const value = localFilters[key];
        return value && (
            (typeof value === 'string' && value.trim()) ||
            (Array.isArray(value) && value.length > 0) ||
            (typeof value === 'object' && value !== null && (value.min || value.max))
        );
    }).length;

    const headerActions = (
        <>
            <div className="filter-count">
                {activeFilterCount > 0 && (
                    <span>{activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''} active</span>
                )}
            </div>
            <div className="action-buttons">
                <Button
                    onClick={handleClear}
                    variant="outline"
                    disabled={activeFilterCount === 0}
                >
                    Clear All
                </Button>
                <Button
                    onClick={handleApply}
                    variant="primary"
                >
                    Apply Filters
                </Button>
            </div>
        </>
    );

    return (
        <Modal onClose={onClose} title="Filter Members" headerActions={headerActions}>
            <div className="filter-modal-content">
                <div className="filter-fields">
                    {!config?.filters || config.filters.length === 0 ? (
                        <div className="no-filters-message">
                            <p>No filters have been configured for this form.</p>
                            <p><em>Configure filters in the admin panel to enable filtering.</em></p>
                        </div>
                    ) : (
                        config.filters.map(renderFilterField)
                    )}
                    
                    {/* Location-based filters if enabled */}
                    {config?.config?.enable_location_filters && (
                        <div className="location-filters">
                            <h4>Location Filters</h4>
                            <div className="filter-field">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={localFilters.has_location || false}
                                        onChange={(e) => handleFilterChange('has_location', e.target.checked)}
                                    />
                                    Only show members with location
                                </label>
                            </div>
                            
                            <div className="filter-field">
                                <label htmlFor="distance_from">Distance from location</label>
                                <div className="distance-filter">
                                    <input
                                        type="text"
                                        id="location_search"
                                        placeholder="Enter city or address..."
                                        value={localFilters.location_search || ''}
                                        onChange={(e) => handleFilterChange('location_search', e.target.value)}
                                        className="filter-input"
                                    />
                                    <select
                                        value={localFilters.distance_radius || ''}
                                        onChange={(e) => handleFilterChange('distance_radius', e.target.value)}
                                        className="filter-select distance-select"
                                    >
                                        <option value="">Any distance</option>
                                        <option value="5">Within 5 km</option>
                                        <option value="10">Within 10 km</option>
                                        <option value="25">Within 25 km</option>
                                        <option value="50">Within 50 km</option>
                                        <option value="100">Within 100 km</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
}

export default FilterModal;
